// Taşeron portalı — `UploadModal`'ın sunum parçaları (dosya adı başlıkları, reddedilen / yüklenemeyen
// dosya listeleri, yapay zekâ açıklama kutusu). Durum TAŞIMAZLAR; hepsi `UploadModal`'dan prop alır.

import { Button, MAX_ANALYSIS_FILES, MAX_ANALYSIS_TOTAL_BYTES } from '@isgportal/shared';
import type { TFunction } from 'i18next';
import { Check, CircleAlert, TriangleAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { UploadQueueItem } from '../lib/DocumentAnalysisProvider';

/** `DocumentAnalysisTray.tsx:106-112`'deki AYNI mantığın yerel kopyası — o dosya bu görevin
 * kapsamı DIŞINDA (dokunulmaz), fonksiyon dışa aktarılamıyor; saf/altı satırlık bir yardımcı
 * olduğu için kopyalamak paylaşılan bir modüle taşımaktan daha ucuz. */
function splitFileName(name: string): { stem: string; ext: string } {
  const idx = name.lastIndexOf('.');
  if (idx <= 0 || idx === name.length - 1) {
    return { stem: name, ext: '' };
  }
  return { stem: name.slice(0, idx), ext: name.slice(idx) };
}

export type RejectReason =
  | 'DOCUMENT_TOO_LARGE'
  | 'DOCUMENT_TYPE_UNSUPPORTED'
  | 'overCap'
  | 'overSizeCap';

export interface RejectedFile {
  id: string;
  name: string;
  reason: RejectReason;
}

/** Parti bayt tavanının görünen etiketi — SABİTTEN türetilir, metne gömülmez. Üç dilde de
 * aynı ("60 MB"); birim kısaltmaları zaten dilden bağımsızdır (bkz. `format.ts:formatBytes`)
 * ve tavan tam sayı MB olduğundan `formatBytes`in bir ondalıklı biçimi ("60,0 MB") burada
 * gereksiz bir hassasiyet gösterirdi — `documents.dropHint`/`upload.ai.box.l4` da "60 MB" der. */
const TOTAL_BYTES_LABEL = `${Math.round(MAX_ANALYSIS_TOTAL_BYTES / (1024 * 1024))} MB`;

/** Tek dal, iki durum: dosya adı HER ZAMAN kırpılır (uzantı sabit kalır), çoklu seçimde
 * arkasına "+N dosya" eklenir. Eskiden çoklu dal adı HAM interpole ediyordu ve uzun bir ad üç
 * satıra sarıyordu (tasarım N5); iki dal tek gövdede birleşince o fark ortadan kalkar. */
export function FileSummaryHeader({ files }: { files: File[] }) {
  const { t } = useTranslation();
  const first = files[0];
  if (!first) {
    return null;
  }
  const { stem, ext } = splitFileName(first.name);
  return (
    <p className="flex items-baseline text-sm font-medium text-fg">
      <span className="min-w-0 truncate">{stem}</span>
      <span className="shrink-0">{ext}</span>
      {files.length > 1 ? (
        <span className="ml-1 shrink-0">
          {t('upload.modal.filesSummaryMore', { count: files.length - 1 })}
        </span>
      ) : null}
    </p>
  );
}

export function FileGroupLabel({ name }: { name: string }) {
  const { stem, ext } = splitFileName(name);
  return (
    <p className="mb-3 flex items-baseline text-sm font-medium text-fg">
      <span className="min-w-0 truncate">{stem}</span>
      <span className="shrink-0">{ext}</span>
    </p>
  );
}

/** Reddedilen dosyanın gerekçe metni. İki PARTİ tavanı kendi mesajını sabitten interpolasyonla
 * kurar (metne gömülü rakam YOK); kalan iki gerekçe zaten backend hata KODUdur ve doğrudan
 * çözülür (`error.*` yolu, `t(reason)`). Sayı tavanı ile BAYT tavanı AYRI mesajlardır —
 * ikisi için tek bir metin kullanmak kullanıcıya YANLIŞ kuralı söylerdi. */
function rejectionMessage(reason: RejectReason, t: TFunction): string {
  if (reason === 'overCap') {
    return t('upload.rejected.overCap', { max: MAX_ANALYSIS_FILES });
  }
  if (reason === 'overSizeCap') {
    return t('upload.rejected.overSize', { max: TOTAL_BYTES_LABEL });
  }
  return t(reason);
}

export function RejectionList({ rejected }: { rejected: RejectedFile[] }) {
  const { t } = useTranslation();
  if (rejected.length === 0) {
    return null;
  }
  return (
    <div className="rounded-md border border-danger/30 bg-danger-subtle p-3">
      <p className="flex items-center gap-2 text-sm font-medium text-danger">
        <TriangleAlert className="h-4 w-4 shrink-0" aria-hidden="true" />
        {t('upload.rejected.title', { count: rejected.length })}
      </p>
      <ul className="mt-1.5 flex flex-col gap-0.5 pl-6 text-xs text-danger">
        {rejected.map((item) => (
          <li key={item.id}>
            {item.name} — {rejectionMessage(item.reason, t)}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** §8 yüklemesi başarısız kalmış kuyruk öğeleri — her satır dosyayı ADIYLA anar (owner
 * raporu madde 1) ve kendi "Tekrar dene"sini taşır (madde 4). Bir öğe başarıyla tekrar
 * yüklenince `retriedUuids` dolar ve `stillFailedItems`'tan düşer — bu bileşen artık onu
 * göstermez, `manualGroups` bir elle-giriş kartı olarak devralır. */
export function FailedUploadList({
  items,
  retryingIds,
  onRetry,
  disabled,
}: {
  items: UploadQueueItem[];
  retryingIds: Set<string>;
  onRetry: (item: UploadQueueItem) => void;
  disabled: boolean;
}) {
  const { t } = useTranslation();
  if (items.length === 0) {
    return null;
  }
  return (
    <div className="rounded-md border border-danger/30 bg-danger-subtle p-3">
      <p className="flex items-center gap-2 text-sm font-medium text-danger">
        <CircleAlert className="h-4 w-4 shrink-0" aria-hidden="true" />
        {t('upload.failed.title', { count: items.length })}
      </p>
      <p className="mt-0.5 pl-6 text-xs text-danger">{t('upload.failed.hint')}</p>
      <ul className="mt-1.5 flex flex-col gap-1.5 pl-6">
        {items.map((item) => (
          <li key={item.id} className="flex items-center justify-between gap-2 text-xs text-danger">
            <span className="min-w-0 truncate">{item.name}</span>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 shrink-0 px-2 text-xs"
              onClick={() => onRetry(item)}
              loading={retryingIds.has(item.id)}
              disabled={disabled || retryingIds.has(item.id)}
              aria-label={t('upload.failed.retryItem', { name: item.name })}
            >
              {t('analysis.tray.retry')}
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function ExplainerBox() {
  const { t } = useTranslation();
  return (
    <div className="rounded-md bg-surface-muted p-4">
      <p className="text-xs font-medium text-muted">{t('upload.ai.box.title')}</p>
      <ul className="mt-2 flex flex-col gap-1.5 text-sm text-fg">
        {(['l1', 'l2', 'l3'] as const).map((key) => (
          <li key={key} className="flex items-start gap-2">
            <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-success" aria-hidden="true" />
            {t(`upload.ai.box.${key}`)}
          </li>
        ))}
      </ul>
      {/* `l4` (10 dosya / 60 MB tavanı) LİSTEDEN çıktı: yeşil ✓ bir FAYDA satırıdır, tavan ise
          bir sınır — üstelik aynı kural `documents.dropHint`te zaten yazıyor (tasarım N5).
          Kutunun SON satırı yine `box.confirm`dir (turkish-ohs-expert RC-4 kararı, bkz.
          `locales.ts`), o yüzden tavan onun ÜSTÜNE, sessiz bir satır olarak iner. */}
      <p className="mt-3 text-xs text-muted">{t('upload.ai.box.l4')}</p>
      <p className="mt-1 text-xs text-muted">{t('upload.ai.box.confirm')}</p>
    </div>
  );
}
